"use client";

import { isEthereumWallet } from "@dynamic-labs/ethereum";
import { useDynamicContext } from "@dynamic-labs/sdk-react-core";
import { Button, ButtonSize, ButtonVariant, cn, evaluate } from "@kyan/ui";
import { type ReactNode } from "react";

import { useWalletButtonFeatureFlag } from "#/hooks/featureFlags/useWalletButtonFeatureFlag";
import { useChainId } from "#/hooks/useChainId";
import { useHandleLogIn } from "#/hooks/useHandleLogIn";
import { getChainName } from "#/utils/chain";

export interface ConnectWalletWrapperProps {
  children: ReactNode;
  className?: string;
  chainId?: number;
  size?: ButtonSize;
  variant?: ButtonVariant;
}

export const ConnectWalletWrapper: React.FC<ConnectWalletWrapperProps> = ({
  children,
  className,
  chainId,
  size = "lg",
  variant = "primary",
}) => {
  const { handleLogIn } = useHandleLogIn();
  const { chainId: currentChainId } = useChainId();
  const { isEnabled, title } = useWalletButtonFeatureFlag();
  const { primaryWallet, network, showAuthFlow } = useDynamicContext();

  const effectiveChainId = chainId ?? currentChainId;

  const isDisabledByFeatureFlag = !isEnabled;
  const disabledLabel = title || "Connect Wallet";
  const buttonLabel = evaluate({
    "Connecting...": showAuthFlow,
    [disabledLabel]: isDisabledByFeatureFlag,
    "Connect Wallet": true,
  });

  const classNames = cn(
    "mt-2 w-full text-[15px] whitespace-nowrap shadow-none",
    className,
  );

  const switchNetwork = async () => {
    if (!primaryWallet || !isEthereumWallet(primaryWallet)) {
      return;
    }
    await primaryWallet.switchNetwork(effectiveChainId);
  };

  if (!primaryWallet) {
    return (
      <Button
        size={size}
        variant={variant}
        className={classNames}
        disabled={isDisabledByFeatureFlag}
        onClick={handleLogIn}
      >
        {buttonLabel}
      </Button>
    );
  }

  if (!network) {
    return (
      <Button disabled size={size} variant={variant} className={classNames}>
        Connecting...
      </Button>
    );
  }

  if (Boolean(network) && effectiveChainId !== Number(network)) {
    return (
      <Button
        size={size}
        variant={variant}
        className={cn(classNames, "min-w-55")}
        onClick={switchNetwork}
      >
        {`Switch to ${getChainName(effectiveChainId)}`}
      </Button>
    );
  }

  return children;
};
